import { useContext, useState } from 'react'
import {
  ScorecardCriteriaCommandList,
  ScorecardCriteriaScoreList,
  scaleOptions,
} from '~/features/command-lists'
import { useDatastore } from '~/features/datastore'
import { SavedQueryFilter } from '~/features/saved-query'

import { FilterCollection, getValueAsArray } from '@hap/analytics'
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@hap/components'

import { FilterContext } from '../filter-context'

function getScale(scale: string) {
  return scaleOptions[scale as keyof typeof scaleOptions] || { options: [] }
}

function getCompoundFilter(filter?: SavedQueryFilter) {
  if (!filter) return
  if (filter.compound) {
    const compound = filter.compound[0]
    if (compound.op === 'in') {
      return compound
    }
  }
}

function getCompoundValues(filter?: SavedQueryFilter) {
  const compound = getCompoundFilter(filter)
  if (compound && compound.op === 'in') {
    return compound.values as string[]
  }
  return []
}

export function ScorecardCriteriaFilterControl({
  placeholder = 'Scorecard Criteria',
  initialCriteriaId = null,
}: Props) {
  const { data } = useDatastore(FilterCollection.ScorecardCriteria)
  const { filter, onSelect } = useContext(FilterContext)
  const [criteriaId, setCriteriaId] = useState(initialCriteriaId)
  const criteria = data.find((record) => record.id === criteriaId)
  const values = getCompoundValues(filter)

  const handleSelect = (ids: string[]) => {
    if (!criteriaId) return
    onSelect({
      op: 'eq',
      field: 'scorecardCriteriaId',
      dataset: 'evaluationCriteriaScores',
      value: criteriaId,
      compound: [
        {
          op: 'in',
          field: 'awardedPoints',
          values: ids,
          dataset: 'evaluationCriteriaScores',
        },
      ],
    })
  }

  return criteria ? (
    <ScorecardCriteriaScoreList
      criteriaId={criteria.id}
      onSelect={handleSelect}
      selected={values}
      multiselect
    />
  ) : (
    <ScorecardCriteriaCommandList
      placeholder={placeholder}
      onSelect={(values) => setCriteriaId(values[0] || null)}
    />
  )
}

export function ScorecardCriteriaFilterTrigger(props: {
  onDelete: () => void
  readOnly?: boolean
}) {
  const { data } = useDatastore(FilterCollection.ScorecardCriteria)
  const { filter } = useContext(FilterContext)
  const values = getValueAsArray(filter)
  const criteria = data.find((record) => {
    if (!filter) return false
    if (filter.op === 'eq') return record.id === filter.value
    if (filter.op === 'in') return values.includes(record.id)
    return false
  })
  const scale = criteria ? getScale(criteria.meta.scale) : { options: [] }
  const compoundFilter = getCompoundFilter(filter)
  return (
    <div className="border-b-foreground/22 from-accent/15 to-accent/0 border-input flex items-stretch rounded-md border bg-linear-to-t whitespace-nowrap *:flex *:items-center *:rounded-none [&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md">
      <div className="border-foreground/22 gap-2 border-r px-3">
        <Icon name="medal" /> {criteria?.name}
      </div>
      <div className="border-foreground/22 hover:bg-accent gap-2 border-r px-1">
        <Popover>
          <PopoverTrigger
            disabled={props.readOnly}
            className="flex items-center gap-1 px-1"
          >
            {compoundFilter?.values.map((value, key) => {
              const option = scale.options.find((o) => o.value === value)
              if (!option) return null
              return (
                <Tooltip key={key}>
                  <TooltipTrigger>{option.icon}</TooltipTrigger>
                  <TooltipContent>
                    {option.icon} {option.label}
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </PopoverTrigger>
          <PopoverContent className="p-0">
            <ScorecardCriteriaFilterControl
              initialCriteriaId={values[0] as unknown as string}
            />
          </PopoverContent>
        </Popover>
      </div>
      {!props.readOnly && (
        <Button
          variant="ghost"
          onClick={props.onDelete}
          className="h-full w-full p-0 px-2"
        >
          <Icon name="x" weight="bold" size={12} />
        </Button>
      )}
    </div>
  )
}

interface Props {
  placeholder?: string
  initialCriteriaId?: string | null
}
